// MF-568 — Card 'pat-student-report': the ACER-style student report. Scatter of item position (x) against item
// difficulty (y), by response, a line at the student's scale score, and the items in a sortable table below.
// Rows: { position, item_uid, difficulty, response ('C' | 'X' | '-'), strand, sub_strand, description, scale_score, test_date }.
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, INK, FONT, baseTextStyle, legendBase, tooltipBase } from '../boardTheme'
import { resolveColors, useBoardStyle } from '../boardPalettes'
import { buttonProps, fmtDay, useChartResize } from './patShared'
import { Interpretation } from './PatParts'

const RESPONSES = {
  C: { name: 'Correct', mark: '✓', symbol: 'circle', size: 10 },
  X: { name: 'Incorrect', mark: '✗', symbol: 'triangle', size: 11 },
  '-': { name: 'Not administered', mark: '–', symbol: 'rect', size: 8 }
}
const kindOf = (r) => (r === 'C' || r === 'X' ? r : '-')
const num = (v) =>
  v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v)

const PatStudentReportCard = ({ data }) => {
  const style = useBoardStyle()
  const [blue, orange] = resolveColors(style, {}, 2)
  const colors = { C: blue, X: orange, '-': INK.muted }
  const items = useMemo(
    () =>
      (data?.rows || []).map((r) => ({
        ...r,
        position: num(r.position),
        difficulty: num(r.difficulty),
        kind: kindOf(r.response)
      })),
    [data]
  )
  const first = items[0] || {}
  const score = num(first.scale_score)
  const [sort, setSort] = useState({ key: 'position', dir: 1 })
  const { chartRef, boxRef } = useChartResize()

  const sorted = useMemo(() => {
    const v = [...items]
    v.sort((a, b) => {
      const x = a[sort.key] ?? Infinity
      const y = b[sort.key] ?? Infinity
      return (x - y) * sort.dir || (a.position ?? 0) - (b.position ?? 0)
    })
    return v
  }, [items, sort])

  // ACER gives no difficulty to the items of some tests (Science) and to part of others (Reading): they cannot sit on the
  // difficulty chart, so say so instead of drawing an empty one.
  const withDifficulty = items.filter((i) => i.position !== null && i.difficulty !== null).length
  const noDifficulty = items.length - withDifficulty
  const counts = { C: 0, X: 0, '-': 0 }
  items.forEach((i) => {
    counts[i.kind] += 1
  })

  const option = useMemo(() => {
    const plotted = items.filter((i) => i.position !== null && i.difficulty !== null)
    return {
      textStyle: baseTextStyle,
      animationDuration: 300,
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        formatter: (p) => {
          const i = p.data?.item
          if (!i) return ''
          return `<b>Item ${i.position}</b> · ${RESPONSES[i.kind].name}<br/>Difficulty ${i.difficulty}<br/>${i.strand || ''}${i.description ? `<br/>${i.description}` : ''}`
        }
      },
      legend: { ...legendBase, top: 0 },
      grid: { top: 60, bottom: 40, left: 44, right: 24 },
      xAxis: {
        type: 'value',
        name: 'Item position in the test',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { color: INK.secondary, fontFamily: FONT, fontSize: 11 },
        minInterval: 1,
        splitLine: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.muted, fontSize: 10 }
      },
      yAxis: {
        type: 'value',
        scale: true,
        name: 'Difficulty',
        nameTextStyle: { color: INK.secondary, fontFamily: FONT, fontSize: 11 },
        splitLine: { lineStyle: { type: 'dashed', color: INK.grid } },
        axisLabel: { color: INK.muted, fontSize: 10 }
      },
      series: Object.keys(RESPONSES).map((k, idx) => ({
        name: `${RESPONSES[k].name} (${counts[k]})`,
        type: 'scatter',
        symbol: RESPONSES[k].symbol,
        symbolSize: RESPONSES[k].size,
        itemStyle:
          k === '-' ? { color: '#fff', borderColor: colors[k], borderWidth: 1.5 } : { color: colors[k] },
        data: plotted
          .filter((i) => i.kind === k)
          .map((i) => ({ value: [i.position, i.difficulty], item: i })),
        ...(idx === 0 && score !== null
          ? {
              markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { color: INK.primary, type: 'solid', width: 1.5 },
                label: {
                  formatter: `Scale score ${score}`,
                  position: 'insideEndTop',
                  fontFamily: FONT,
                  fontSize: 11,
                  color: INK.primary
                },
                data: [{ yAxis: score }]
              }
            }
          : {})
      }))
    }
    // counts and colours are derived from items / style
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, score, blue, orange])

  const header = (key, label) => (
    <Box
      role='columnheader'
      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
      {...buttonProps(() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 })))}
      sx={{ cursor: 'pointer', fontWeight: 600, color: SUMMARY_PALETTE.muted, fontSize: 11 }}
    >
      {label}
      {sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </Box>
  )

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>
          Scale score{' '}
          <Box component='span' sx={{ fontSize: 22, fontWeight: 600, color: blue }}>
            {score === null ? 'N/A' : score}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>
          Tested {fmtDay(first.test_date)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
          {items.length} items · {counts.C} correct · {counts.X} incorrect · {counts['-']} not administered
        </Typography>
      </Box>
      {withDifficulty > 0 ? (
        <Box ref={boxRef} sx={{ minWidth: 0 }}>
          <ReactECharts ref={chartRef} option={option} notMerge style={{ height: 280, width: '100%' }} />
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: SUMMARY_PALETTE.muted, my: 1.5 }}>
          ACER does not give the items of this test a difficulty, so the difficulty chart is not drawn. The
          answers are in the table below.
        </Typography>
      )}
      {withDifficulty > 0 && noDifficulty > 0 ? (
        <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.faint, mb: 0.5 }}>
          {noDifficulty} of {items.length} items have no difficulty in the data: they are in the table, not on
          the chart.
        </Typography>
      ) : null}
      <Box role='table' aria-label='Items of the sitting' sx={{ mt: 1, fontSize: 12, minWidth: 0 }}>
        <Box
          role='row'
          sx={{
            display: 'grid',
            gridTemplateColumns: '64px 76px 120px minmax(0, 1fr)',
            gap: 1,
            py: 0.5,
            borderBottom: `1px solid ${SUMMARY_PALETTE.cardBorder}`
          }}
        >
          {header('position', 'Item')}
          {header('difficulty', 'Difficulty')}
          <Box role='columnheader' sx={{ fontWeight: 600, color: SUMMARY_PALETTE.muted, fontSize: 11 }}>
            Response
          </Box>
          <Box role='columnheader' sx={{ fontWeight: 600, color: SUMMARY_PALETTE.muted, fontSize: 11 }}>
            Strand and description
          </Box>
        </Box>
        {sorted.map((i) => (
          <Box
            key={`${i.position}-${i.item_uid}`}
            role='row'
            sx={{
              display: 'grid',
              gridTemplateColumns: '64px 76px 120px minmax(0, 1fr)',
              gap: 1,
              py: 0.5,
              borderBottom: `1px solid ${INK.grid}`,
              color: SUMMARY_PALETTE.ink
            }}
          >
            <Box role='cell'>{i.position ?? '–'}</Box>
            <Box role='cell'>{i.difficulty ?? '–'}</Box>
            <Box
              role='cell'
              sx={{
                color: i.kind === 'X' ? '#7a3e12' : i.kind === 'C' ? '#24407a' : SUMMARY_PALETTE.muted,
                fontWeight: 600
              }}
            >
              {RESPONSES[i.kind].mark} {RESPONSES[i.kind].name}
            </Box>
            <Box role='cell' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              <b>{i.strand || '–'}</b>
              {i.sub_strand && i.sub_strand !== i.strand ? ` · ${i.sub_strand}` : ''}
              {i.description ? <Box sx={{ color: SUMMARY_PALETTE.muted }}>{i.description}</Box> : null}
            </Box>
          </Box>
        ))}
      </Box>
      <Interpretation />
    </Box>
  )
}

export default PatStudentReportCard
